<template>
    <tailwind-layout title="Maintenance Dashboard">
        <template #header>

        <alert-card v-if="is_property_count_zero && is_landlord_invited && is_invitation_accepted" type="warning" class="mx-8 my-3">
            <span>
                Your tenant has invited you to set up an account in the RentRisk Maintenance Portal. In this platform your tenants will be able to send maintenance requests, upload photos, and other communication about any maintenance issues they are having. It also allows you to provide updates and keeps a record of maintenance issues. Please start <inertia-link :href="route('maintenance.create')" class="text-blue-600 hover:text-blue-800 underline">here</inertia-link> to create your property on the maintenance platform.
            </span>
        </alert-card>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Maintenance Dashboard</h1>
                    <h2 class="text-xs">This is where you track and manage properties for maintenance.</h2>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link class="primaryButton" :href="route('maintenance.create')" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-start md:items-center">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">{{ $page.props.auth.user.name }}'s Maintenance Dashboard</h1>
                        <h2 class="text-xs">This is where you track and manage properties for maintenance.</h2>
                    </div>
                    <inertia-link class="primaryButton" :href="route('maintenance.create')" data-demo-target="add-property-btn">
                        Add Property
                    </inertia-link>
                </div>
            </div>
        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of Properties per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search for Maintenance Properties..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                        <div class="flex items-center mt-2 md:ml-2" @click="show_archived_properties()">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Properties</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" v-if="status === 'open' || status === 'submitted'">
                                <div class="flex">
                                    <div class="flex-shrink-0">
                                        <font-awesome-icon :icon="faInfoCircle" class="text-yellow-400 dark:text-yellow-300" />
                                    </div>
                                    <div class="ml-3 flex-1 md:flex md:justify-between">
                                        <p class="text-sm text-yellow-700">
                                            <span v-if="status === 'submitted'">
                                                You are currently viewing properties with new maintenance requests that have been submitted by tenants.
                                            </span>
                                            <span v-if="status === 'open'">
                                                You are currently viewing properties with open maintenance requests that are either submitted or in progress.
                                            </span>
                                        </p>
                                        <inertia-link :href="route('maintenance.index')" class="whitespace-nowrap font-bold text-sm text-yellow-700 hover:text-yellow-600 dark:text-yellow-300 dark:hover:text-yellow-200">
                                            Show All Properties
                                            <span aria-hidden="true"> &rarr;</span>
                                        </inertia-link>
                                    </div>
                                </div>
                            </div>
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table data-demo-target="properties-table" class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Address
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900">
                                            <span>
                                                Tenant
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900">
                                            Maintenance Requests
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900">
                                            Requests Status
                                        </th>
                                        <!-- <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-right">
                                            <span @click="sortTable('status')">
                                                Status
                                                <icon v-show="form.sort_by === 'status'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'status'" :name="'sort'" />
                                            </span>
                                        </th> -->
                                        <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                                            Actions
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                    <tr v-for="(property, index) in properties.data" :key="index"
                                        :class="[(index % 2 === 0 && !property.deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!property.deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                        (property.deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                    >
                                        <td @click.stop="showEditProperty(property)"
                                        class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <inertia-link :href="route('maintenance.edit', property.id)"
                                                class="text-blue-600 hover:text-blue-800 underline">
                                                {{ property.full_address }}
                                            </inertia-link>
                                        </td>
                                        <td @click.stop="showRequests(property)"
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                            {{ property.tenant_name === false ? '-' : property.tenant_name }}
                                            <div v-if="!property.is_invitation_accepted && property.tenant_name" class="text-left">
                                                <button @click="showResendInvitationPopup(property)" type="button" class="ml-2 text-sm text-indigo-600 hover:text-indigo-500 font-medium underline float-right">Resend Invitation</button>
                                            </div>
                                        </td>
                                        <td @click.stop="showRequests(property)"
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                            {{ property.maintenance_requests_count }}
                                        </td>
                                        <td @click.stop="showRequests(property)"
                                            class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-center">
                                                <ul>
                                                    <li v-for="open_request in property.open_requests" :key="open_request.status" :class="[getStatusColor(open_request.status),'my-1 flex justify-between']">
                                                        <span>{{ getStatusName(open_request.status) }}</span> <span class="pl-4">({{ open_request.count }})</span>
                                                    </li>
                                                </ul>
                                        </td>
                                        <!-- <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                                 <span :class="[property.status === 'Active' ? 'text-green-700 bg-green-50 ring-green-600/20' : 'text-red-700 bg-red-50 ring-red-600/10', 'rounded-md py-1 px-2 text-xs font-medium ring-1 ring-inset' ]">
                                                     {{ property.status }}
                                                 </span>
                                            </span>
                                            <span v-if="property.deleted_at" class="text-red-700 bg-red-50 ring-red-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset">
                                                Archived
                                            </span>
                                        </td> -->
                                        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 text-right">
                                            <inertia-link v-if="!property.deleted_at" :href="route('maintenance.edit', property.id)"
                                                class="rounded-l-md relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Edit Property
                                            </inertia-link>
                                            <button v-if="!property.deleted_at" @click="openDeleteModal(property)"  class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                Archive
                                            </button>

                                            <inertia-link v-if="!property.deleted_at"
                                                :href="route('maintenance-requests.index', { maintenance_property: property.id })"
                                                class="relative rounded-r-md  -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10"
                                                data-demo-target="maintenance-requests"
                                                >
                                                Requests
                                            </inertia-link>

                                            <button v-if="property.deleted_at" @click="openRestoreModal(property)"
                                                class="relative rounded-r-md rounded-l-md -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                Restore
                                            </button>
                                            </td>
                                    </tr>
                                    <tr v-show="!properties.data.length">
                                        <td colspan="5" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No Properties were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(property, index) in properties.data" :key="index" :class="[property.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border flex flex-col']">
                                        <inertia-link :href="route('maintenance-requests.index', property.id)">
                                            <div class="flex flex-col p-6">
                                                <h3 class="truncate text-sm font-medium text-gray-900">{{ property.full_address }}</h3>
                                                <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Tenant:</span> {{ property.tenant_name || 'N/A' }}</h4>
                                                <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Total Requests:</span> {{ property.maintenance_requests_count }}</h4>
                                                <ul class="flex flex-row space-x-2">
                                                    <li v-for="open_request in property.open_requests" :key="open_request.status" :class="[getStatusColor(open_request.status),'my-1 justify-between w-fit']">
                                                        <span>{{ getStatusName(open_request.status) }}</span> <span class="pl-4">({{ open_request.count }})</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </inertia-link>
                                        <!-- Middle Spacer (Grows to push content and buttons apart) -->
                                        <div class="flex-grow min-w-0"></div>
                                        <div class="flex-none">
                                            <div :class="[property.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <inertia-link v-if="!property.deleted_at" :href="route('maintenance.edit', property.id)" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-edit" />
                                                        Edit
                                                    </inertia-link>
                                                    <button v-if="!property.deleted_at" @click="openDeleteModal(property)"  class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                        Archive
                                                    </button>
                                                    <inertia-link v-if="!property.deleted_at" :href="route('maintenance-requests.index', { maintenance_property: property.id })" :class="[property.deleted_at ? 'hover:bg-red-200' : 'hover:bg-blue-200' , 'relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900']">
                                                        <font-awesome-icon icon="fa-trash" />
                                                        Requests
                                                    </inertia-link>
                                                    <button v-if="property.deleted_at" @click="openRestoreModal(property)"
                                                        class="bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-blue-200 rounded-md">
                                                        Restore
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="properties.links" :results="properties" :type="'Maintenance Properties'" />

            <!-- Delete Confirmation Modal -->
            <div v-if="showDeleteModal">
                <TransitionRoot as="template" :show="showDeleteModal">
                    <Dialog as="div" class="relative z-10" @close="showDeleteModal = false">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                    <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-6 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                        <div class="text-center sm:text-left">
                                            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                                <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                            </div>
                                            <div class="mt-2">
                                                <p class="text-sm text-center sm:mt-5">
                                                    Are you sure you want to archive "<strong>{{ selectedProperty?.full_address }}</strong>"?
                                                </p>
                                            </div>
                                        </div>

                                        <div class="mt-5 sm:mt-6 flex">
                                            <button type="button" class="w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none" @click="showDeleteModal = false">
                                                Cancel
                                            </button>
                                            <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="deleteProperty">
                                                Confirm
                                            </button>
                                        </div>
                                    </DialogPanel>
                                </TransitionChild>
                            </div>
                        </div>
                    </Dialog>
                </TransitionRoot>
            </div>

            <!-- Restore Modal  -->
            <div v-if="showRestoreModal">
                <TransitionRoot as="template" :show="showRestoreModal">
                    <Dialog as="div" class="relative z-10" @close="closeVendorModal">
                        <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </TransitionChild>

                        <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                            <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                                <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                    <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                        <div>
                                            <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                                <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                            </div>
                                            <div class="mt-3 text-center sm:mt-5">
                                                <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this property?</DialogTitle>
                                                <div class="mt-2">
                                                </div>
                                            </div>
                                        </div>
                                        <div class="mt-5 sm:mt-6 flex">
                                            <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600" @click="showRestoreModal = false">Close</button>
                                            <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="restoreProperty">Confirm</button>
                                        </div>
                                    </DialogPanel>
                                </TransitionChild>
                            </div>
                        </div>
                    </Dialog>
                </TransitionRoot>
            </div>

        </div>

        <resend-invitation :show="showResendInvitationModal" @close="showResendInvitationModal = false" @confirm="resendInvitation"></resend-invitation>
    
    <!-- Demo Widget -->
    <DemoWidget
        :page-identifier="'maintenance.index'"
        :steps="demoSteps"
        :auto-start="true"
        :show-help-icon="true"
    />
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import {Switch, SwitchLabel, SwitchGroup} from "@headlessui/vue";
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowsRotate, faBoxArchive } from '@fortawesome/pro-solid-svg-icons';
import { router } from '@inertiajs/vue3';
import AlertCard from '@/Shared/AlertCard.vue';
import ResendInvitation from '@/Shared/ResendInvitation.vue'
import DemoWidget from '@/Components/DemoWidget.vue'
import { getDemoSteps } from '@/Config/demoSteps.js'
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";


export default {
    components: {
        SwitchGroup,
        SwitchLabel,
        Switch,
        Button,
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon,
        AlertCard,
        AlertCard,
        ResendInvitation,
        DemoWidget,
        FontAwesomeIcon
    },
    props: {
        filters: Object,
        properties: Object,
        people: Object,
        is_landlord_invited: Boolean,
        is_invitation_accepted: Boolean,
        is_property_count_zero: Boolean,
        status: String,
    },

    data() {
        return {
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:100,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            selectedProperty: null,
            demoSteps: getDemoSteps('maintenance.index'),
            showDeleteModal: false,
            showRestoreModal: false,
            showDeleteModal: false,
            showRestoreModal: false,
            propertyToRestore: null,
            faArrowsRotate,
            showResendInvitationModal: false,
            faInfoCircle
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('maintenance.index'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        editProperty(id) {
            this.$inertia.get(this.route("maintenance.edit", id));
        },
        show_archived_properties() {
            this.form.trashed = !this.form.trashed;
        },
        openDeleteModal(property) {
            this.selectedProperty = property;
            this.showDeleteModal = true;
        },
        deleteProperty() {
            if (!this.selectedProperty) return;
            this.$inertia.delete(this.route('maintenance.destroy', this.selectedProperty.id), {
                preserveState: true,
                onSuccess: () => {
                    this.showDeleteModal = false;
                    this.selectedProperty = null;
                },
                onError: () => {
                }
            });
        },
        openRestoreModal(property) {
            this.propertyToRestore = property;
            this.showRestoreModal = true;
        },
        restoreProperty() {
            if (!this.propertyToRestore) return;
            router.put(route('maintenance.restore', this.propertyToRestore.id), {}, {
                onSuccess: () => {
                    this.showRestoreModal = false;
                    this.propertyToRestore = null;
                },
                onError: (errors) => {
                    console.error("Restore failed:", errors);
                    alert('Failed to restore property.');
                }
            });
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        showRequests(property) {
            if(property.maintenance_requests_count === 0) return;
            this.selectedProperty = property;
            this.$inertia.get(route('maintenance-requests.index', { maintenance_property: property.id }));
        },
        showEditProperty(property) {
            this.$inertia.get(this.route("maintenance.edit", property.id));
        },

        showResendInvitationPopup(property) {
            this.selectedProperty = property;
            this.showResendInvitationModal = true;
        },

        resendInvitation() {
            this.$inertia.post(this.route('maintenace.resend-invitation-to-tenant', this.selectedProperty.id), {},{
                onFinish: () => {
                    this.showResendInvitationModal = false;
                }
            })
        },

        getStatusColor(status) {
            const colors = {
                'submitted': 'text-yellow-700 bg-yellow-50 ring-yellow-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
                'in_progress': 'text-blue-700 bg-blue-50 ring-blue-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset',
            };
            return colors[status] || 'text-green-700 bg-green-50 ring-green-700 rounded-md py-1 px-2 text-xs font-bold ring-1 ring-inset';
        },
        getStatusName(status) {
            const names = {
                'submitted': 'Submitted',
                'in_progress': 'In Progress',
            };
            return names[status];
        },
    },
}
</script>
